/**
 * Выбор характеристики — общий для скан-экранов.
 *
 * ⚠️ Ответ отдаётся ВАРИАНТОМ, а не развёрнутым значением, потому что диалекта
 * ДВА и оба законные. Экраны, которые копят черновик (сборка, перемещения),
 * разворачивают его сами через `charAnswer`: в позицию и в документ 1С уезжает
 * ПУСТАЯ ссылка. Экраны, которые переспрашивают сервер (инвентаризация,
 * коробки), шлют ссылку КАК ЕСТЬ — там `none` и есть ответ, а пустая строка
 * читается сервером как «человек не ответил», и вопрос пошёл бы по кругу.
 *
 * ⚠️ Вариант «без характеристики» приходит С СЕРВЕРА, ссылкой `none`, и это
 * полноценный ответ: у нас 95 % строк регистра штрихкодов идут с пустой
 * характеристикой, то есть «никакая» — самый частый правильный выбор. Пустую
 * строку сервер не отличил бы от «человек не ответил» и спросил бы снова.
 *
 * ⚠️ Экраны сборки и перемещений про эту ссылку НЕ ЗНАЛИ: они рисовали список
 * кнопок как есть и рядом — свой чекбокс «Без характеристики». Пока сервер
 * такой вариант отдавал только приёмке возвратов и коробкам, это не всплывало;
 * как только он появился на главном пути сборки, нажатие кнопки отправило бы
 * в позицию строку `none` вместо ссылки — и она уехала бы в расходную 1С.
 * Чекбокс при этом дублировал ту же кнопку, а когда сервер вариант НЕ давал
 * (у товара характеристика есть у всех кодов), позволял записать ответ,
 * которого данные не подтверждают.
 */
import { Loader2 } from "lucide-react";

import type { CharOption } from "@/api/order_check";
import { cn } from "@/lib/utils";

/** Ссылка варианта «без характеристики». Зеркалит `scanning.NO_CHARACTERISTIC`. */
export const NO_CHARACTERISTIC = "none";

/** Ответ человека в том виде, в каком его хранит экран и принимает бэк. */
export interface CharAnswer {
  charRef: string;
  charName: string;
  withoutChar: boolean;
}

/** Выбранный вариант → ответ. `none` разворачивается в «характеристики нет». */
export function charAnswer(option: CharOption): CharAnswer {
  return option.ref === NO_CHARACTERISTIC
    ? { charRef: "", charName: "", withoutChar: true }
    : { charRef: option.ref, charName: option.name, withoutChar: false };
}

/** Выбран ли этот вариант сейчас. */
export function isPicked(option: CharOption, answer: CharAnswer): boolean {
  return option.ref === NO_CHARACTERISTIC
    ? answer.withoutChar
    : answer.charRef === option.ref;
}

export function CharacteristicChoice({
  options,
  answer,
  onPick,
  required,
  busy,
}: {
  options: CharOption[];
  answer: CharAnswer;
  /** ⚠️ Отдаём ВЫБРАННЫЙ ВАРИАНТ, а не развёрнутый ответ. Экранам он нужен
   *  по-разному: сборка и перемещения кладут в черновик развёрнутый
   *  (`charAnswer`), а инвентаризация шлёт ссылку на сервер КАК ЕСТЬ — там
   *  `none` и есть ответ, пустая строка означала бы «не ответил». */
  onPick: (option: CharOption) => void;
  /** Подсветить: без ответа скан не пройдёт. */
  required?: boolean;
  /** ⚠️ Ответ уже улетел на сервер — кнопки заблокированы до ответа.
   *  Передают только экраны, у которых выбор УХОДИТ ЗАПРОСОМ (инвентаризация,
   *  коробки): POST скана отвечает около секунды, и за эту секунду человек
   *  успевал нажать второй раз — в прод-БД лежат пары сканов одного кода с
   *  интервалом 0.4–1.0 с, часть — в РАЗНЫЕ кнопки, то есть две строки одной
   *  номенклатуры в документе 1С. Экраны с локальным черновиком (сборка,
   *  перемещения) ничего не ждут и проп не передают.
   *  ⚠️ Вместе с блокировкой ОБЯЗАТЕЛЬНА видимая подпись «Записываю…»: без неё
   *  мёртвые кнопки читаются как зависший портал, и по ним бьют сильнее. */
  busy?: boolean;
}): React.ReactElement | null {
  if (options.length === 0) return null;
  return (
    <div className={cn("rounded-lg p-2", required && "border border-amber-200 bg-amber-50")}>
      <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-amber-700">
        <span>Характеристика:</span>
        {busy && (
          <span className="flex items-center gap-1 text-slate-500">
            <Loader2 aria-hidden className="size-3 animate-spin" />
            Записываю…
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {options.map((c) => (
          <button
            key={c.ref}
            type="button"
            disabled={busy}
            // Блокировка кнопки — не единственная защита: сюда приходят и
            // программные клики (ТСД, автотесты), а второй ответ на тот же
            // скан даёт вторую строку в документе 1С.
            onClick={() => {
              if (busy) return;
              onPick(c);
            }}
            className={cn(
              "h-11 rounded-lg border text-[14px]",
              isPicked(c, answer)
                ? "border-sky-700 bg-sky-700 text-white"
                : "border-slate-300",
              c.ref === NO_CHARACTERISTIC && !isPicked(c, answer) && "text-slate-500",
              busy && "opacity-50",
            )}
          >
            {c.ref === NO_CHARACTERISTIC ? "Без характеристики" : c.name}
          </button>
        ))}
      </div>
    </div>
  );
}
